import { useEffect,} from "react";
import Matter from "matter-js";
import './spoondrop.css';
import { swapDocBody, useGameState, MAX_HEIGHT, MAX_WIDTH, getSpoon, getDroppingIndicator } from "./util/spoonHelper";
import { createMatterEngine } from "./util/createMatterEngine";
import { GameShell } from "./util/GameShell";
import { createPausableTimerGroup } from "./util/pausableTimers";

const SpoonDropRescue = () => {
  const gameKey = "rescue";
  const flavor = "AHHH! Spoons are falling from the skyyyyy." ;
  const instructions = "Click or tap and drag to move the net. Catch all the spoons!";
  const gameState = useGameState(flavor, instructions, gameKey);
  const { canvasRef, setCanvasHeight, restartRef, setPlayButtonText,
    setGameOverState, setMessage, setScoreText, recordScore,
    gameStartedRef, rebuildKey, pauseRef, resumeRef,} = gameState;

  useEffect(() => swapDocBody(), []);
  useEffect(() => {
    const {
      Bodies,
      Body,
      Composite,
      Composites,
      Constraint,
      Mouse,
      MouseConstraint,
      Vector,
      Events,
    } = Matter;

    const allSpoons = [];
    const spoonsInQueue = [];
    const indicators = [];
    var isMobile = false;

    const width = Math.min(window.innerWidth, MAX_WIDTH);
    const height = Math.min(window.innerHeight, MAX_HEIGHT);
    setCanvasHeight(height);
    
    const timers = createPausableTimerGroup();
    const { engine, runner, render, start, cleanup } = createMatterEngine(timers, canvasRef, width, height);

    var gameWidth = width*8/10;
    var margin = width/10;
    var fric = 0.2;
    engine.gravity.y = 0.3;
    var restitutionValue = 0.8;
    var size = 100; // size var for spoon

    // Trampoline
    var segmentCount = 11;
    var segmentWidth = 40;
    var segmentHeight = 30;
    const startX = width / 2 - (segmentCount * segmentWidth) / 2;
    const startY = height * 0.8;

    if (width < 900) {
      isMobile = true;
      size = width > 500 && height > 500 ? 75 : 50;
      fric = 0.03;
      segmentCount = 7;
      segmentWidth = 28;
      segmentHeight = 15;
      gameWidth = width * 9/10;
      margin = width/20;
    } 



    const trampoline = Composites.stack(
      startX,
      startY,
      segmentCount,
      1,
      0,
      0,
      (x, y) =>
        Bodies.rectangle(x, y, segmentWidth, segmentHeight, {
          restitution: restitutionValue,
          frictionAir: fric,
          friction: fric,
          collisionFilter: {
            group:2,
            category: 2, 
            mask: 4
          },
          render: { fillStyle: "#558B2F" },
          chamfer: { radius: 6 },
        })
    );

    Composites.chain(trampoline, 0.5, 0, -0.5, 0, {
      stiffness: 1,
      length: 2,
      render: { visible: false },
    });

    // Rails at ends (invisible, kinematic)
    const leftRail = Bodies.rectangle(startX, startY, 10, 10, {
      isStatic: false,
      isSensor: true,
      collisionFilter: { group: -1 },
      render: { visible: false },
    });
    Body.setVelocity(leftRail, { x: 0, y: 0 });
    leftRail.isSleeping = true;

    const rightRail = Bodies.rectangle(
      startX + segmentCount * segmentWidth,
      startY,
      10,
      10,
      {
        isStatic: false,
        isSensor: true,
        collisionFilter: { group: -1 },
        render: { visible: false },
      }
    );
    Body.setVelocity(rightRail, { x: 0, y: 0 });
    rightRail.isSleeping = true;

    Composite.add(engine.world, [leftRail, rightRail]);

    const leftConstraint = Constraint.create({
      bodyA: trampoline.bodies[0],
      pointA: { x: -segmentWidth / 2, y: 0 },
      bodyB: leftRail,
      pointB: { x: 0, y: 0 },
      length: 0,
      stiffness: 1,
      render: { visible: false },
    });

    const rightConstraint = Constraint.create({
      bodyA: trampoline.bodies[segmentCount - 1],
      pointA: { x: segmentWidth / 2, y: 0 },
      bodyB: rightRail,
      pointB: { x: 0, y: 0 },
      length: 0,
      stiffness: 1,
      render: { visible: false },
    });

    trampoline.bodies.forEach((body, index) => {
      body.label = index;
      console.log(index)
    })

    Composite.add(engine.world, [leftConstraint, rightConstraint]);
    Composite.add(engine.world, trampoline);

    // Mouse and dragging trampoline horizontally with damping
    const mouse = Mouse.create(render.canvas);
    const mouseConstraint = MouseConstraint.create(engine, {
      mouse,
      constraint: {
        stiffness: 0.2,
        render: { visible: false },
      },
    });
    Composite.add(engine.world, mouseConstraint);

    // Variables for smooth movement
    let isDragging = false;
    //controls
    Events.on(mouseConstraint, "mousedown", () => {
      isDragging = true;
    });
    Events.on(mouseConstraint, "mouseup", () => {
      isDragging = false;
    });
    
    // Movement Code
    // Lock rails vertical position & velocity + smooth horizontal movement toward targetX
    Events.on(engine, "beforeUpdate", () => {
      if (isDragging) {
        let totalSpoonMass = 0;
        
        allSpoons.forEach(spoon => {
          if (!spoon.isSleeping) {
            totalSpoonMass += spoon.mass;
          }
        });
    
        const maxMass = 50;
        const weightFactor = Math.min(totalSpoonMass / maxMass, 1);
        const dampingBase = 0.2;
        const boost = weightFactor * 0.5;
        const damping = dampingBase + boost;
    
        const targetX = mouse.position.x;
        const currentCenterX = (leftRail.position.x + rightRail.position.x) / 2;
    
        const newCenterX = currentCenterX + (targetX - currentCenterX) * damping;
        const halfWidth = (segmentCount * segmentWidth) / 3;
    
        const newLeftX = newCenterX - halfWidth;
        const newRightX = newCenterX + halfWidth;
    
        Body.setPosition(leftRail, Vector.create(newLeftX, startY));
        Body.setVelocity(leftRail, Vector.create(0, 0));
    
        Body.setPosition(rightRail, Vector.create(newRightX, startY));
        Body.setVelocity(rightRail, Vector.create(0, 0));
      } else {
        // When not dragging, allow X drift but lock Y
        Body.setPosition(leftRail, Vector.create(leftRail.position.x, startY));
        Body.setVelocity(leftRail, Vector.create(leftRail.velocity.x, 0));
    
        Body.setPosition(rightRail, Vector.create(rightRail.position.x, startY));
        Body.setVelocity(rightRail, Vector.create(rightRail.velocity.x, 0));
      }
    
      // Lock only the leftmost and rightmost trampoline segments in Y
      const leftSegment = trampoline.bodies[0];
      const rightSegment = trampoline.bodies[trampoline.bodies.length - 1];
    
      Body.setPosition(leftSegment, Vector.create(leftSegment.position.x, startY));
      Body.setVelocity(leftSegment, Vector.create(leftSegment.velocity.x, 0));
    
      Body.setPosition(rightSegment, Vector.create(rightSegment.position.x, startY));
      Body.setVelocity(rightSegment, Vector.create(rightSegment.velocity.x, 0));
    });
    Events.on(engine, "collisionStart", function(event) {
      const pairs = event.pairs;
      for (const pair of pairs) {
        checkPointIncrement(pair)
      }
    });
    
    // accounts for spoons when game is over
    var deadSpoons = [];

    // Game state vars
    var dropSpoons;
    var points = 0;
    var tracker = 0;
    var initialSpeed = 50;
    var speed = initialSpeed;
    

    // Spawn falling spoons function
    function getRandomInt(max) {
      return Math.floor(Math.random() * max);
    }

    function spawnFallingSpoon() {
      let obstacleSize = size * 0.800 + size * Math.random() * 0.4; //slight variation to spawn size
      let indicatorShowing = false;
      let nextDropXPos = gameWidth * Math.random() + margin; 
      let indicatorTimeTracker = 10;
      let spoonType = getRandomInt(5);
      let color = spoonColors[spoonType];
      let triangleBody = getDroppingIndicator(nextDropXPos, color, isMobile);
      indicators.push(triangleBody);
      let indicatorBlinkingInterval = timers.setInterval(() => {
        if(indicatorShowing){
          Composite.remove(engine.world, triangleBody)
        }
        else{
          Composite.add(engine.world, triangleBody)
        }
        indicatorShowing = !indicatorShowing;
        indicatorTimeTracker--;
        if(indicatorTimeTracker<=0){
          let spoonObstacle = getFallingSpoon(obstacleSize, nextDropXPos, spoonType);
          
          spoonObstacle.counted = false;
          allSpoons.push(spoonObstacle);
          //remove old spoons so there arent too many in the net
          if (allSpoons.length > 4) {//max number of spoons allowed in game
            const oldSpoon = allSpoons.shift(); // remove first (oldest) spoon
            Composite.remove(engine.world, oldSpoon);
          }
          Composite.add(engine.world, spoonObstacle);
          timers.clearInterval(indicatorBlinkingInterval);
          spoonsInQueue.shift();
          indicators.shift();
        }
      }, 500);
      spoonsInQueue.push(indicatorBlinkingInterval)
    }
    function clearAllQueuedSpoons(){
      spoonsInQueue.forEach(interval => {
        timers.clearInterval(interval)
      })
      spoonsInQueue.splice(0, spoonsInQueue.length);
      indicators.forEach(body => {
        Composite.remove(engine.world, body);
      })
      indicators.splice(0, indicators.length);
    }

    //slight variation on density of spoon based on color
    const spoonColors = ["#2f6f8b", "#6f2f8b","#ad3f1a", "#a1ad1a", "#ad1a1a"];
    const spoonGrav = [0.0011, 0.0012, 0.0008, 0.0009, 0.001];

    //returns a spoon to fall from top of screen
    function getFallingSpoon(obstacleSize, xposSpawn, spoonType){
      
      let spoonDensity = spoonGrav[spoonType];
      let color = spoonColors[spoonType];
      let collisionFilter = {
          group: -2,
          category: 4,
          mask: 2,
        }
      let spoonObstacle = getSpoon(obstacleSize, xposSpawn, -100, collisionFilter, color );
      
      spoonObstacle.parts.forEach(part => {
        part.restitution = restitutionValue;
        part.density = spoonDensity;
      });
      
      //ANGLE
      let angle = Math.random() * 90 - 45;
          
      Body.setAngle(spoonObstacle, angle);

      if (Math.random() * 2 < 1 ) {//some of em spin :)
        Body.setAngularVelocity(
          spoonObstacle,
          ((obstacleSize - Math.random() * obstacleSize) / 600) * (getRandomInt(3) - 1)
        );
      }

      return spoonObstacle;
    }

    function detectDroppedSpoons(){
      let droppedSpoons = false;
      allSpoons.forEach((element) => {
        if (element.position.y > height + 100) {
          droppedSpoons = true;
          Composite.remove(engine.world, element);
        }
      });
      if(droppedSpoons && gameStarted){
        gameOver();
         //deal with spoons so they can fly up and then get deleted
        allSpoons.forEach((element) => {
          deadSpoons.push(element);
        });
        allSpoons.splice(0, allSpoons.length);
      }
    }
    function checkPointIncrement(collisionPair){
      const { bodyA, bodyB } = collisionPair;
      //bodyA is trampoline segment bodyB is spoon
      if(bodyA.label > 0 && bodyA.label < segmentCount-1){
        if(bodyB.parent.counted === false){
          points++;
          bodyB.parent.counted = true;
        }
      }
      //bodyA is spoon bodyB is trampoline
      if(bodyB.label > 0 && bodyB.label < segmentCount-1){
        if(bodyA.parent.counted === false){
          points++;
          bodyA.parent.counted = true;
        }
      }
    }

    // Game control vars
    var gameStarted = false;
    var resettable = false;

    function restartGame() {
      if (resettable === true) {
        points = 0;
        setScoreText(points);            // reset score
        resettable = false;
        tracker = 0;
        speed = initialSpeed;
        //removes spoons from previous game if applicable
        if(deadSpoons.length > 0){
          deadSpoons.forEach((element) => {
            Composite.remove(engine.world, element);
          });
          deadSpoons.splice(0, deadSpoons.length);
        }

        // Reset trampoline rails to center
        Body.setPosition(leftRail, Vector.create(startX, startY));
        Body.setPosition(rightRail, Vector.create(startX + segmentCount * segmentWidth, startY));
        startGame();
      }
    }
    //TODO: add more messages
    function gameOver() {
      recordScore(points);
      setScoreText(points + " spoons saved");
      let endMessage = getPopupMessage()
      setMessage(endMessage);
      clearAllQueuedSpoons();
      gameStarted = false;
      gameStartedRef.current = false;
      resettable = true;
      timers.clearInterval(dropSpoons);
      let dumpForce = isMobile ? 0.1: 0.5;
      trampoline.bodies.forEach(segment => {
        const netForce = { x: 0, y: dumpForce }; // throw out the spoons
        Body.applyForce(segment, segment.position, netForce);
      });
      const tutEl = document.getElementById("descenttut");
      if (tutEl) tutEl.innerHTML = "";
      const dropperEl = document.getElementById("dropper");
      if (dropperEl) dropperEl.innerHTML = "";
      timers.setTimeout(() => {
        setGameOverState(true); // Show game over screen
      }, 1100)
    }
    var debug = false;
    function startGame() {
      if (gameStarted === false) {
        setGameOverState(false); // Show game over screen
        gameStarted = true;
        gameStartedRef.current = true;
        const tutEl = document.getElementById("descenttut");
        if (tutEl) tutEl.innerHTML = "";

        dropSpoons = timers.setInterval(() => {
          if (document.getElementById("dropper") === null) {
            timers.clearInterval(dropSpoons);
            return;
          }
          detectDroppedSpoons()
          if(doSpawnCheck()){
            spawnFallingSpoon();
          }
          if(gameStarted){
            setText();
          }
        }, 100);
      }
    }
    //handles increasing speed of game spawns
    //returns true if spawn should occur
    function doSpawnCheck() {
      let doSpawn = false;
      //speed change for spoon drops
      //after 12 it's close to max speed
      if (tracker % (3 * speed) === 0 && speed > 25) {
        speed = speed - 5;
        tracker = 0;
      }
      //more gradual speed change to top speed
      else if (tracker % (3 * speed) === 0 && speed > 20) {
        speed--;
        tracker = 0;
      }
      if (tracker % speed === 0) {
        doSpawn = true;
      }
      tracker++;
      return doSpawn; 
    }

    function setText() {
      const dropperEl = document.getElementById("dropper");
      //point tracking messages
      if(points >= 20){
        if (dropperEl) dropperEl.innerHTML = "Whoa! " + points + " spoons saved";
      }
      else if(points >= 10){
        if (dropperEl) dropperEl.innerHTML = "Nice! " + points + " spoons saved"; 
      }
      else{
        if (dropperEl) dropperEl.innerHTML = points + " spoons saved";
      }
      if (dropperEl && debug) dropperEl.innerHTML = "Whoa! " + points + " spoons saved. Speed = " + speed;
    }

    function getPopupMessage(isStart){
      if(isStart){
        return "context!"
      }
      let message;
      if(points >= 20){
        message = "Now we're gonna be able to open that soup kitchen :)";
      }
      else message = "What are we gonna do with all these?";
      return message;
    }
    function startRestart(){
      if(!gameStarted && !resettable){startGame()}
      else{
        restartGame();
      }
      setPlayButtonText("Restart")
    }
    restartRef.current = startRestart;
    pauseRef.current = () => {
      runner.enabled = false;
      timers.pauseAll();
      setScoreText("Score: " + points + " points")
    };
    resumeRef.current = () => {
      runner.enabled = true;
      timers.resumeAll();
    };

    start();
    setGameOverState(true); // Show game over screen
    // Cleanup on unmount
    return cleanup;
  }, [canvasRef, restartRef, setCanvasHeight, setGameOverState, setMessage, setPlayButtonText, setScoreText, recordScore, rebuildKey, gameStartedRef, pauseRef, resumeRef]);

  return (
    <GameShell gameName="Rescue" canvasRef={canvasRef} gameState={gameState} />
  );
};

export default SpoonDropRescue;
